import React, { useEffect, useRef } from "react";
import { ParticipantEvent, Track } from "livekit-client";

const VIDEO_SOURCES = new Set([Track.Source.Camera, Track.Source.ScreenShare]);

const publicationFor = (preferredPublication, source, participant) => {
  if (preferredPublication) return preferredPublication;
  if (!participant) return null;
  return participant.getTrackPublication?.(source || Track.Source.Camera) || null;
};

/**
 * Attach a LiveKit remote video track directly to a muted <video> element.
 *
 * The higher-level LiveKit VideoTrack component can retain an element that is
 * still bound to a replaced iOS camera track after reconnecting. Directly
 * detaching the old track and attaching the current publication makes video
 * recovery independent from the audio renderer and avoids an audio-only
 * viewer session.
 */
const DirectRemoteVideo = ({
  trackRef,
  className = "",
  style,
  testId,
}) => {
  const videoRef = useRef(null);
  const attachedTrackRef = useRef(null);
  const attachedElementRef = useRef(null);
  const participant = trackRef?.participant;
  const preferredPublication = trackRef?.publication;
  const source = trackRef?.source;
  const fallbackTrack = trackRef?.track;

  useEffect(() => {
    let disposed = false;
    let retryTimer = null;

    const detachCurrent = () => {
      const video = videoRef.current || attachedElementRef.current;
      const current = attachedTrackRef.current;
      if (current && video) {
        try {
          current.detach(video);
        } catch (_) {}
      }
      attachedTrackRef.current = null;
      attachedElementRef.current = null;
    };

    const attachCurrent = (eventTrack, eventPublication) => {
      if (disposed || !videoRef.current) return;
      const publication = (
        eventPublication && VIDEO_SOURCES.has(eventPublication.source)
          ? eventPublication
          : publicationFor(preferredPublication, source, participant)
      );
      const nextTrack = (
        typeof eventTrack?.attach === "function" && eventTrack?.kind === Track.Kind.Video
          ? eventTrack
          : publication?.track || fallbackTrack
      );

      if (!nextTrack) {
        detachCurrent();
        return;
      }
      if (attachedTrackRef.current !== nextTrack) {
        detachCurrent();
        nextTrack.attach(videoRef.current);
        attachedTrackRef.current = nextTrack;
        attachedElementRef.current = videoRef.current;
      }

      videoRef.current.muted = true;
      videoRef.current.playsInline = true;
      const playPromise = videoRef.current.play();
      playPromise?.catch?.(() => {
        window.clearTimeout(retryTimer);
        retryTimer = window.setTimeout(() => {
          if (!disposed) videoRef.current?.play?.().catch?.(() => {});
        }, 250);
      });
    };

    const scheduleAttach = (trackOrPublication, publication) => {
      const eventTrack = typeof trackOrPublication?.attach === "function"
        ? trackOrPublication
        : null;
      const eventPublication = publication || (
        VIDEO_SOURCES.has(trackOrPublication?.source)
          ? trackOrPublication
          : null
      );
      window.clearTimeout(retryTimer);
      retryTimer = window.setTimeout(
        () => attachCurrent(eventTrack, eventPublication),
        0,
      );
    };
    const onUnsubscribed = (track) => {
      if (track === attachedTrackRef.current) detachCurrent();
      scheduleAttach();
    };

    attachCurrent();
    participant?.on?.(ParticipantEvent.TrackSubscribed, scheduleAttach);
    participant?.on?.(ParticipantEvent.TrackPublished, scheduleAttach);
    participant?.on?.(ParticipantEvent.TrackUnmuted, scheduleAttach);
    participant?.on?.(ParticipantEvent.TrackUnsubscribed, onUnsubscribed);

    return () => {
      disposed = true;
      window.clearTimeout(retryTimer);
      participant?.off?.(ParticipantEvent.TrackSubscribed, scheduleAttach);
      participant?.off?.(ParticipantEvent.TrackPublished, scheduleAttach);
      participant?.off?.(ParticipantEvent.TrackUnmuted, scheduleAttach);
      participant?.off?.(ParticipantEvent.TrackUnsubscribed, onUnsubscribed);
      detachCurrent();
    };
  }, [fallbackTrack, participant, preferredPublication, source]);

  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      playsInline
      className={className}
      style={style}
      data-testid={testId}
    />
  );
};

export default DirectRemoteVideo;
